/**
 * SeatOperationView.tsx
 * 출석부 좌석 뷰
 *
 * 공유 SeatCanvas 컴포넌트를 사용하여 통합레이아웃과 동일한 방식으로 렌더링
 */

import React, { useMemo } from 'react';
import { SeatCanvas, CanvasSeatData } from '../shared';
import { RuntimeSeat } from '../../repositories/SeatOperationRepository';

// 오늘 연락 메모 정보
export interface TodayMemoInfo {
  student_id: string;
  content: string;
  category: string;
  memo_date: string | null;
}

interface SeatOperationViewProps {
  seats: RuntimeSeat[];
  canvasWidth: number;
  canvasHeight: number;
  onSeatClick: (seatId: string) => void;
  showLegend?: boolean;
  todayMemos?: Record<string, TodayMemoInfo>;  // student_id → memo info
}

export const SeatOperationView: React.FC<SeatOperationViewProps> = ({
  seats,
  canvasWidth,
  canvasHeight,
  onSeatClick,
  showLegend = true,
  todayMemos = {},
}) => {
  // RuntimeSeat → CanvasSeatData 변환
  const seatData: CanvasSeatData[] = useMemo(() => {
    return seats.map(seat => {
      const memo = seat.student_id ? todayMemos[seat.student_id] : undefined;
      return {
        seat_id: seat.seat_id,
        seat_number: seat.seat_number,
        seat_code: seat.seat_code,
        // 좌표 변환: x,y → position_x, position_y
        position_x: seat.x,
        position_y: seat.y,
        // 출석 정보
        student_id: seat.student_id,
        student_name: seat.student_name,
        schedule_status: seat.schedule_status,
        is_late: seat.is_late,
        outing_type: seat.outing_type,
        return_overdue_minutes: seat.return_overdue_minutes,
        expected_check_in: seat.expected_check_in,
        expected_check_out: seat.expected_check_out,
        academy_leave_time: seat.academy_leave_time,
        expected_return_time: seat.expected_return_time,
        // 구역 정보
        zone_id: seat.zone_id,
        zone_color: seat.zone_color || undefined,
        zone_name: seat.zone_name || undefined,
        // 연락 메모
        has_today_memo: !!memo,
        today_memo_content: memo?.content,
        schedule_never_submitted: seat.schedule_never_submitted,
      };
    });
  }, [seats, todayMemos]);

  // 클릭 핸들러
  const handleSeatClick = (seatId: string) => {
    onSeatClick(seatId);
  };

  return (
    <SeatCanvas
      seats={seatData}
      canvasWidth={canvasWidth}
      canvasHeight={canvasHeight}
      mode="attendance"
      onSeatClick={handleSeatClick}
      showLegend={showLegend}
    />
  );
};

export default SeatOperationView;
